// ── Types ──
export type LeadTier = "qualified" | "review" | "notafit";

export interface Option {
  label: string;
  value: string;
}

export interface Question {
  id: string;
  type: "single" | "multi" | "dropdown" | "phq9" | "contact";
  question: string;
  helpText?: string;
  options?: Option[];
  // Show only if this predicate passes
  showIf?: (answers: Record<string, string | string[]>) => boolean;
}

// ── UTM / gclid capture ──
export function captureUtm(): Record<string, string> {
  if (typeof window === "undefined") {
    return { utm_source: "", utm_medium: "", utm_campaign: "", gclid: "" };
  }
  const params = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  );
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    gclid: params.get("gclid") || "",
  };
}

// ── Session ID (random, no health data) ──
export function sessionId(): string {
  return (
    "screener-" +
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10)
  );
}

// ── PHQ-9 standard items ──
export const PHQ9_ITEMS: string[] = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling/staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself — that you are a failure",
  "Trouble concentrating on things, such as reading or watching TV",
  "Moving or speaking so slowly that others could have noticed — or the opposite, being fidgety or restless",
  "Thoughts that you would be better off dead or of hurting yourself in some way",
];

export const PHQ9_OPTIONS: Option[] = [
  { label: "Not at all", value: "0" },
  { label: "Several days", value: "1" },
  { label: "More than half the days", value: "2" },
  { label: "Nearly every day", value: "3" },
];

// ── Question flow (no safety screen, no bipolar/substance/state questions) ──
export const QUESTIONS: Question[] = [
  // Age
  {
    id: "age",
    type: "single",
    question: "What is your age range?",
    options: [
      { label: "Under 18", value: "under18" },
      { label: "18–21", value: "18-21" },
      { label: "22–80", value: "22-80" },
      { label: "Over 80", value: "over80" },
    ],
  },
  // Depression diagnosis
  {
    id: "depression_dx",
    type: "single",
    question:
      "Have you been diagnosed with depression by a doctor or mental health professional?",
    options: [
      { label: "Yes — Major Depressive Disorder", value: "mdd" },
      { label: "Yes — Treatment-Resistant Depression", value: "trd" },
      { label: "Yes — other or not sure of the name", value: "other" },
      { label: "No", value: "no" },
    ],
  },
  // Current episode
  {
    id: "current_episode",
    type: "single",
    question: "Are you currently experiencing a depressive episode?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
  },
  // Meds tried
  {
    id: "meds_count",
    type: "single",
    question: "How many antidepressant medications have you tried?",
    options: [
      { label: "0", value: "0" },
      { label: "1", value: "1" },
      { label: "2–3", value: "2-3" },
      { label: "4 or more", value: "4+" },
    ],
  },
  // 30 days (conditional on meds >= 1)
  {
    id: "meds_30days",
    type: "single",
    question: "Did you take each of those medications for at least 30 days?",
    showIf: (a) => {
      const v = a["meds_count"];
      return Boolean(v && v !== "0");
    },
    options: [
      { label: "Yes, all of them", value: "all" },
      { label: "Some of them", value: "some" },
      { label: "No", value: "no" },
      { label: "Not sure", value: "notsure" },
    ],
  },
  // Why stopped (conditional, multi)
  {
    id: "meds_stopped_reason",
    type: "multi",
    question: "Why did you stop? (Select all that apply)",
    showIf: (a) => {
      const v = a["meds_count"];
      return Boolean(v && v !== "0");
    },
    options: [
      { label: "Didn't help enough", value: "nohelp" },
      { label: "Side effects", value: "sideeffects" },
      { label: "Still taking them", value: "still" },
      { label: "Other", value: "other" },
    ],
  },
  // Psychiatrist
  {
    id: "psychiatrist",
    type: "single",
    question: "Have you been evaluated by a psychiatrist?",
    options: [
      { label: "Yes, currently under care", value: "current" },
      { label: "Yes, in the past", value: "past" },
      { label: "No, only a primary care doctor", value: "pcp" },
      { label: "No", value: "no" },
    ],
  },
  // Talk therapy
  {
    id: "talk_therapy",
    type: "single",
    question: "Have you tried talk therapy for depression?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
  },
  // Other treatments
  {
    id: "other_treatments",
    type: "multi",
    question: "Have you tried any of these? (Select all that apply)",
    options: [
      { label: "Standard TMS", value: "tms" },
      { label: "Ketamine or Spravato", value: "ketamine" },
      { label: "ECT", value: "ect" },
      { label: "None", value: "none" },
    ],
  },
  // 15. PHQ-9
  {
    id: "phq9",
    type: "phq9",
    question:
      "Over the last 2 weeks, how often have you been bothered by these problems?",
  },
  // 16. Contact
  {
    id: "contact",
    type: "contact",
    question: "Almost done — where can we reach you?",
    helpText:
      "A member of our clinical team will reach out within one business day.",
  },
];

// ── Scoring ──
export function computePhq9(
  answers: Record<string, string | string[]>,
): number {
  const phq = answers["phq9"];
  if (!phq || typeof phq !== "string") return 0;
  // stored as "0,1,2,3,0,1,2,3,0"
  return phq.split(",").reduce((sum, v) => sum + (parseInt(v, 10) || 0), 0);
}

export function isCrisis(answers: Record<string, string | string[]>): boolean {
  const phq = answers["phq9"];
  if (!phq || typeof phq !== "string") return false;
  const items = phq.split(",");
  // item 9 (index 8) — anything other than "0" is a crisis trigger
  return items[8] !== undefined && items[8] !== "0";
}

export function getNotAFitReason(
  answers: Record<string, string | string[]>,
): string {
  const age = answers["age"];
  const dx = answers["depression_dx"];

  if (age === "under18") {
    return "SAINT® is cleared for adults 22 and older. If you're under 18, we'd recommend speaking with a child psychiatrist or your pediatrician about other options that may help.";
  }
  if (dx === "no") {
    return "SAINT® is designed for adults who have been diagnosed with depression that hasn't improved enough with medication. If you haven't been diagnosed yet, we'd recommend starting with a consultation with a psychiatrist or your primary care doctor.";
  }
  return "Based on your answers, SAINT® may not be the right fit right now. We'd be happy to discuss other treatment options that may help.";
}

export function scoreLead(answers: Record<string, string | string[]>): {
  tier: LeadTier;
  reason: string;
} {
  const age = answers["age"];
  const dx = answers["depression_dx"];
  const episode = answers["current_episode"];
  const medsCount = answers["meds_count"];
  const meds30 = answers["meds_30days"];
  const stoppedReason = answers["meds_stopped_reason"];
  const psych = answers["psychiatrist"];
  const phqScore = computePhq9(answers);

  // ── HARD STOPS ──
  if (age === "under18") {
    return { tier: "notafit", reason: getNotAFitReason(answers) };
  }
  if (dx === "no") {
    return { tier: "notafit", reason: getNotAFitReason(answers) };
  }

  // ── NEEDS CLINICAL REVIEW ──
  const reviewReasons: string[] = [];
  if (age === "18-21" || age === "over80") reviewReasons.push("age");
  if (medsCount === "0" || medsCount === "1")
    reviewReasons.push("limited medication trials");
  if (phqScore < 10) reviewReasons.push("low symptom severity");
  if (psych === "pcp" || psych === "no")
    reviewReasons.push("no psychiatrist involvement");

  if (reviewReasons.length > 0) {
    return {
      tier: "review",
      reason: `Flagged for clinical review: ${reviewReasons.join(", ")}.`,
    };
  }

  // ── LIKELY CANDIDATE ──
  const hasDx = dx === "mdd" || dx === "trd";
  const inEpisode = episode === "yes";
  const enoughMeds = medsCount === "2-3" || medsCount === "4+";
  const medsAdequate = meds30 === "all";
  const stoppedForReason =
    typeof stoppedReason === "string" &&
    (stoppedReason.includes("nohelp") || stoppedReason.includes("sideeffects"));
  const seenPsych = psych === "current" || psych === "past";
  const phqOk = phqScore >= 10;

  if (
    hasDx &&
    inEpisode &&
    enoughMeds &&
    medsAdequate &&
    stoppedForReason &&
    seenPsych &&
    phqOk
  ) {
    return {
      tier: "qualified",
      reason: "Meets likely-candidate criteria.",
    };
  }

  // Default to review if not clearly qualified
  return {
    tier: "review",
    reason:
      "Does not meet all likely-candidate criteria; needs clinical review.",
  };
}
